import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Ticket, Check } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    amount: 10,
    max_uses: "",
    expires_at: "",
    applies_to_tier: "",
  });

  const load = async () => {
    const { data } = await api.get("/admin/coupons");
    setCoupons(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        code: form.code.toUpperCase().trim(),
        discount_type: form.discount_type,
        amount: parseFloat(form.amount),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        applies_to_tier: form.applies_to_tier || null,
      };
      await api.post("/admin/coupons", body);
      toast.success("Coupon created");
      setForm({ code: "", discount_type: "percent", amount: 10, max_uses: "", expires_at: "", applies_to_tier: "" });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (c) => {
    await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
    await load();
  };

  const remove = async (c) => {
    if (!confirm(`Delete coupon ${c.code}?`)) return;
    await api.delete(`/admin/coupons/${c.id}`);
    await load();
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="eyebrow mb-2">Promotions</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="admin-coupons-title">Coupon Codes</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2" data-testid="new-coupon-button">
          <Plus className="w-4 h-4" /> New coupon
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-coupons-grid">
        {coupons.length === 0 && (
          <div className="bento p-12 text-center text-slate-500 lg:col-span-3">
            <Ticket className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            No coupons yet. Create one for your AppSumo launch.
          </div>
        )}
        {coupons.map((c) => {
          const remaining = c.max_uses != null ? c.max_uses - c.uses : null;
          return (
            <div key={c.id} className={`bento p-6 ${c.active ? "" : "opacity-60"}`} data-testid={`coupon-${c.code}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="eyebrow mb-1">{c.discount_type === "percent" ? "Percent off" : "Flat off"}</p>
                  <h3 className="text-3xl font-bold font-mono text-slate-900 tracking-tight">{c.code}</h3>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                  {c.active ? "active" : "disabled"}
                </span>
              </div>
              <p className="text-4xl font-bold text-slate-900 mb-4">
                {c.discount_type === "percent" ? `${c.amount}%` : `$${c.amount.toFixed(2)}`} <span className="text-base font-medium text-slate-500">off</span>
              </p>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between"><span className="text-slate-500">Used</span><span className="font-mono">{c.uses}{c.max_uses != null ? ` / ${c.max_uses}` : " / ∞"}</span></div>
                {c.applies_to_tier && <div className="flex justify-between"><span className="text-slate-500">Tier</span><span className="font-mono">{c.applies_to_tier}</span></div>}
                {c.expires_at && <div className="flex justify-between"><span className="text-slate-500">Expires</span><span className="font-mono text-xs">{new Date(c.expires_at).toLocaleDateString()}</span></div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleActive(c)} className="flex-1 text-xs px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg" data-testid={`toggle-${c.code}`}>
                  {c.active ? "Disable" : "Enable"}
                </button>
                <button onClick={() => remove(c)} className="text-red-500 hover:bg-red-50 px-3 py-2 rounded-lg" data-testid={`delete-${c.code}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-coupon-modal">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight">New coupon</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Code</label>
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="LAUNCH50" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 font-mono uppercase tracking-widest" data-testid="coupon-code-input" />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Type</label>
                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3" data-testid="coupon-type-select">
                  <option value="percent">Percent (%)</option>
                  <option value="fixed">Fixed ($)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Amount</label>
                <input type="number" step="0.01" min="0" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3" data-testid="coupon-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Max uses</label>
                <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3" data-testid="coupon-max-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Plan tier</label>
                <select value={form.applies_to_tier} onChange={(e) => setForm({ ...form, applies_to_tier: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3" data-testid="coupon-tier-select">
                  <option value="">All tiers</option>
                  <option value="Tier1">Starter (Tier 1)</option>
                  <option value="Tier2">Growth (Tier 2)</option>
                  <option value="Tier3">Scale (Tier 3)</option>
                  <option value="Tier4">Agency (Tier 4)</option>
                </select>
              </div>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Expires (optional)</label>
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-6" data-testid="coupon-expires-input" />
            <button type="submit" className="w-full bg-slate-900 text-white rounded-full py-3 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center justify-center gap-2" data-testid="create-coupon-submit">
              <Check className="w-4 h-4" /> Create coupon
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
